// The map markers (R1291, R1294, player-reach.md §2, ui-map-interface.md
// §17.3.66): the rules and the shapes that the chart piece, the atlas bake
// and the marker menu share.
//
// This is a leaf (`leaves.test.ts`). It imports the generated wire enums and
// the seeded draw of `world.ts`, and nothing of the engine or the DOM. The
// shapes write into a `PathSink`, which `CanvasRenderingContext2D` satisfies,
// so the atlas bakes a cell and the menu paints a target with the same path.
//
// Every shape is ported from mockup round 062
// (`archive/mockups/062-map-markers.html`): `glyphPath` is its `glyphPath`,
// `pencilLoop` is its `loop`, and `mulberry32` of `world.ts` is its seeded
// draw. A
// constant here is a PROPORTION of a figure that `chart.toml` carries: the
// quotient says what the card wrote.

import { MarkerAudience, MarkerKind, type MapMarker } from '../gen/proto/aquaphobia';
import type { MapMarkerStyle } from '../ui/settings';
import { mulberry32 } from '../world';
import { clamp01 } from './math';

/** The four kinds, as the mockup and the atlas keys name them. */
export const MARKER_KIND_SLUGS = ['attack', 'defend', 'gather', 'danger'] as const;
export type MarkerKindSlug = (typeof MARKER_KIND_SLUGS)[number];

/** The two audiences, as the atlas keys and the setting name them. */
export const MARKER_AUDIENCES = ['squad', 'faction'] as const;
export type MarkerAudienceSlug = (typeof MARKER_AUDIENCES)[number];

/** The wire kind of each slug, in the order of `MARKER_KIND_SLUGS`. */
const KIND_WIRE: Record<MarkerKindSlug, MarkerKind> = {
  attack: MarkerKind.MARKER_KIND_ATTACK,
  defend: MarkerKind.MARKER_KIND_DEFEND,
  gather: MarkerKind.MARKER_KIND_GATHER,
  danger: MarkerKind.MARKER_KIND_DANGER,
};

/** The slug of a wire kind, or null for a kind this build does not know. */
export function kindSlug(k: MarkerKind): MarkerKindSlug | null {
  for (const s of MARKER_KIND_SLUGS) if (KIND_WIRE[s] === k) return s;
  return null;
}

/** The wire kind of a slug. */
export function kindWire(s: MarkerKindSlug): MarkerKind {
  return KIND_WIRE[s];
}

/** The slug of a wire audience. Anything but SQUAD reads as the faction. */
export function audienceSlug(a: MarkerAudience): MarkerAudienceSlug {
  return a === MarkerAudience.MARKER_AUDIENCE_SQUAD ? 'squad' : 'faction';
}

/** The wire audience of a slug. */
export function audienceWire(a: MarkerAudienceSlug): MarkerAudience {
  return a === 'squad'
    ? MarkerAudience.MARKER_AUDIENCE_SQUAD
    : MarkerAudience.MARKER_AUDIENCE_FACTION;
}

/**
 * The looks that move on every frame (the cost class of round 062): the
 * pulse ring of card 03 and the flare column of card 10. The other three
 * are a cheap batch, which packs again only when the list, the look or an
 * age step changes (§10.3).
 */
export const LIVE_LOOKS: ReadonlySet<MapMarkerStyle> = new Set<MapMarkerStyle>([
  'pulse-ring', 'flare-column',
]);

/**
 * How far through its life a marker is: 0 when it is new, 1 at its end.
 *
 * `lifeSec` is `CatalogRules.marker_life_sec`, which the server sends. A
 * client that holds no figure yet (0) draws every marker as new, which is
 * the picture a marker has for the first seconds of its life anyway.
 */
export function markerAge(expiresUnix: number, lifeSec: number, nowSec: number): number {
  if (!(lifeSec > 0)) return 0;
  const left = expiresUnix - nowSec;
  return clamp01(1 - left / lifeSec);
}

/** Has the life of a marker ended on this client's wall clock? */
export function markerExpired(m: MapMarker, nowSec: number): boolean {
  return m.expiresUnix > 0 && m.expiresUnix <= nowSec;
}

/**
 * The own marker a press names, or null (R1294).
 *
 * A marker is named when it stands on the pressed cell, or when its cell
 * centre is inside `tolWorld` of the pressed point. The nearest one wins,
 * so two markers on neighbouring cells never swap. Only a marker of
 * `playerId` counts: nobody but the owner can remove one (player-reach.md
 * §2.7).
 */
export function ownMarkerAt(
  list: readonly MapMarker[], playerId: number, cell: number,
  wx: number, wy: number, tolWorld: number,
  centre: (cell: number) => readonly [number, number],
): MapMarker | null {
  if (playerId === 0) return null;
  let best: MapMarker | null = null;
  let bestD = Infinity;
  for (const m of list) {
    if (m.ownerId !== playerId) continue;
    const [x, y] = centre(m.cell);
    const d = m.cell === cell ? 0 : Math.hypot(x - wx, y - wy);
    if (d > tolWorld) continue;
    if (d < bestD) { best = m; bestD = d; }
  }
  return best;
}

/** The part of a 2D context a shape writes into. */
export interface PathSink {
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  quadraticCurveTo(cx: number, cy: number, x: number, y: number): void;
  arc(x: number, y: number, r: number, a0: number, a1: number, ccw?: boolean): void;
  rect(x: number, y: number, w: number, h: number): void;
  closePath(): void;
}

/**
 * The glyph of one kind, `s` being the half extent (round 062 `glyphPath`).
 *
 * An arrow for attack, a shield for defend, a funnel over a dot for gather,
 * and a triangle with a bar and a dot for danger. The four shapes differ in
 * outline, so the kinds read apart in greyscale. The caller fills it with
 * the even-odd rule, which cuts the bar and the dot out, or strokes it.
 */
export function glyphPath(c: PathSink, k: MarkerKindSlug, s: number): void {
  c.beginPath();
  if (k === 'attack') {
    c.moveTo(0, -s); c.lineTo(s * 0.8, -s * 0.1); c.lineTo(s * 0.3, -s * 0.1);
    c.lineTo(s * 0.3, s); c.lineTo(-s * 0.3, s); c.lineTo(-s * 0.3, -s * 0.1);
    c.lineTo(-s * 0.8, -s * 0.1); c.closePath();
  } else if (k === 'defend') {
    c.moveTo(-s * 0.85, -s * 0.85); c.lineTo(s * 0.85, -s * 0.85); c.lineTo(s * 0.85, -s * 0.05);
    c.quadraticCurveTo(s * 0.85, s * 0.7, 0, s);
    c.quadraticCurveTo(-s * 0.85, s * 0.7, -s * 0.85, -s * 0.05); c.closePath();
  } else if (k === 'gather') {
    c.moveTo(-s * 0.9, -s * 0.9); c.lineTo(s * 0.9, -s * 0.9); c.lineTo(s * 0.28, s * 0.05);
    c.lineTo(-s * 0.28, s * 0.05); c.closePath();
    c.moveTo(s * 0.34, s * 0.62); c.arc(0, s * 0.62, s * 0.34, 0, Math.PI * 2);
  } else {
    c.moveTo(0, -s); c.lineTo(s * 0.95, s * 0.8); c.lineTo(-s * 0.95, s * 0.8); c.closePath();
    c.rect(-s * 0.12, -s * 0.38, s * 0.24, s * 0.66);
    c.moveTo(s * 0.14, s * 0.52); c.arc(0, s * 0.52, s * 0.14, 0, Math.PI * 2);
  }
}

/** The points of one loop run (the mockup's 30 steps, and 3 to close it). */
const LOOP_STEPS = 30;
const LOOP_OVERLAP = 3;
/** Where the pencil starts on the loop, in radians before three o'clock. */
const LOOP_START = 0.4;
/** The loop is a little flatter than a circle, as a hand draws it. */
const LOOP_FLAT = 0.92;

/**
 * The hand-drawn loop of card 05, as points round the cell centre.
 *
 * The seed is the marker's own, so one marker keeps its wobble for its
 * whole life and two markers do not wobble alike. `jag` adds the
 * eleven-fold jag of the danger loop.
 */
export function pencilLoop(r: number, seed: number, jag: boolean): [number, number][] {
  const rnd = mulberry32(seed);
  const out: [number, number][] = [];
  for (let i = 0; i <= LOOP_STEPS + LOOP_OVERLAP; i++) {
    const a = (i / LOOP_STEPS) * Math.PI * 2 - LOOP_START;
    const k = 1 + 0.07 * Math.sin(3 * a + seed)
      + (jag ? 0.12 * Math.sin(11 * a) : 0) + (rnd() - 0.5) * 0.05;
    out.push([Math.cos(a) * r * k, Math.sin(a) * r * k * LOOP_FLAT]);
  }
  return out;
}

/** The seeds of the two loops of card 05, off the marker's own seed. */
export const PENCIL_SEED_OFFSET = 3;
export const PENCIL_SEED2_OFFSET = 9;
